import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ProfilePage.css';
import API_BASE_URL from '../config';
import { isProducerLikeRole } from '../utils/talentPoolNavigation';

const ProfilePage = () => {
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    company: '',
    education: '',
    phone: '',
    occupation: '',
    skill: '',
    cityLocation: '',
    profileImageUrl: '',
    resumeUrl: '',
    linkedInUrl: '',
    instagramUrl: '',
    twitterUrl: '',
    youtubeUrl: '',
    portfolioUrl: '',
    careerAspirations: '',
    isPremium: false,
    visibilityLevel: 'standard',
    explorationAreas: [],
    mediaActivity: '',
    openHouseReason: '',
    heardAbout: '',
    heardAboutOther: '',
    consentAcknowledged: false
  });
  const [uploadStatus, setUploadStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    try {
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        if (isProducerLikeRole(userData.userType)) {
          navigate('/profiles', { replace: true });
          return;
        }
        setUser(userData);
      }
      if (token) {
        fetchProfile(token);
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('Error parsing user data:', err);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchProfile = async (token) => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/profiles/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.profile) {
          setProfile({
            name: data.profile.Name || '',
            email: data.profile.Email || '',
            company: data.profile.Company || '',
            education: data.profile.Education || '',
            phone: data.profile.Phone || '',
            occupation: data.profile.Occupation || data.profile.Designation || '',
            skill: data.profile.Skill || '',
            cityLocation: data.profile.CityLocation || '',
            profileImageUrl: data.profile.ProfileImageUrl || '',
            resumeUrl: data.profile.ResumeUrl || '',
            linkedInUrl: data.profile.LinkedInUrl || '',
            instagramUrl: data.profile.InstagramUrl || '',
            twitterUrl: data.profile.TwitterUrl || '',
            youtubeUrl: data.profile.YouTubeUrl || '',
            portfolioUrl: data.profile.PortfolioUrl || '',
            careerAspirations: data.profile.CareerAspirations || '',
            isPremium: data.profile.IsPremium || false,
            visibilityLevel: data.profile.VisibilityLevel || 'standard',
            explorationAreas: data.profile.ExplorationAreas || [],
            mediaActivity: data.profile.MediaActivity || '',
            openHouseReason: data.profile.OpenHouseReason || '',
            heardAbout: data.profile.HeardAbout || '',
            heardAboutOther: data.profile.HeardAboutOther || '',
            consentAcknowledged: data.profile.ConsentAcknowledged || false
          });
        }
      } else if (res.status === 404) {
        // Profile doesn't exist yet, that's okay
        console.log('No profile found, user can create one');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };


  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : (name === 'phone' ? value.replace(/\D/g, '') : value);
    setProfile(prev => ({
      ...prev,
      [name]: nextValue
    }));
  };

  const toggleExplorationArea = (area) => {
    setProfile(prev => {
      const areas = prev.explorationAreas || [];
      const exists = areas.includes(area);
      return {
        ...prev,
        explorationAreas: exists
          ? areas.filter(a => a !== area)
          : [...areas, area],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');

    // Frontend validation for last three mandatory questions
    if (!profile.explorationAreas || profile.explorationAreas.length === 0) {
      setAlertMessage('Please select at least one area you are interested in exploring.');
      return;
    }
    if (!profile.mediaActivity) {
      setAlertMessage('Please select your current media activity status.');
      return;
    }
    if (!profile.openHouseReason || profile.openHouseReason.trim() === '') {
      setAlertMessage('Please tell us why you want to join the MEE School Open House.');
      return;
    }
    if (!profile.heardAbout) {
      setAlertMessage('Please tell us how you heard about this Open House.');
      return;
    }
    if (profile.heardAbout === 'Other' && (!profile.heardAboutOther || profile.heardAboutOther.trim() === '')) {
      setAlertMessage('Please specify how you heard about this Open House.');
      return;
    }
    if (!profile.consentAcknowledged) {
      setAlertMessage('Please confirm that you understand and agree to the participation terms.');
      return;
    }
    try {
      setUploadStatus('Saving profile...');

      const endpoint = token ? `${API_BASE_URL}/api/profiles` : `${API_BASE_URL}/api/open-house-registrations`;
      const headers = token
        ? {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          }
        : {
            'Content-Type': 'application/json',
          };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(profile),
      });

      let data;
      try {
        data = await res.json();
      } catch (jsonErr) {
        console.error('Error parsing JSON response:', jsonErr);
        setAlertMessage('❌ Save failed: Invalid response from server');
        setUploadStatus('Save failed.');
        return;
      }

      if (res.ok) {
        if (token) {
          setUploadStatus('✅ Profile saved successfully!');
          setAlertMessage('Profile saved successfully. You can now leave this page or continue editing.');
        } else {
          setUploadStatus('✅ Registration submitted successfully!');
          setAlertMessage('Registration submitted successfully. You can now leave this page.');
        }
      } else {
        setAlertMessage(`❌ Save failed: ${data?.error || 'Unknown error'}`);
        setUploadStatus('Save failed.');
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      setAlertMessage('❌ Save failed');
      setUploadStatus('Save failed.');
    }
  };

  if (loading) {
    return (
      <div className="marketplace-container">
        <div style={{ textAlign: 'center', padding: '3rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div className="marketplace-container">
      {/* Custom alert modal - no URL shown */}
      {alertMessage && (
        <div className="profile-alert-overlay" onClick={() => setAlertMessage(null)}>
          <div className="profile-alert-box" onClick={(e) => e.stopPropagation()}>
            <p className="profile-alert-message">{alertMessage}</p>
            <button type="button" className="profile-alert-ok" onClick={() => setAlertMessage(null)}>OK</button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="marketplace-header">
        <div className="logo-left">
          <img src="/p3.jpg" alt="Cinepreneur Logo" className="cinepreneur-logo" />
        </div>
        <div className="header-content">
          <h1 className="marketplace-title">CREATOR'S ARCH</h1>
          <p className="marketplace-subtitle">EMPOWERING CREATORS, PROFESSIONALS</p>
        </div>
        <div className="logo-right">
          <img src="/p1.jpg" alt="MeeSchool Logo" className="meeschool-logo" />
        </div>
      </header>

      <div className="marketplace-body">
        {/* Left Navigation Sidebar */}
        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => navigate('/')}>HOME</div>
          <div className="nav-item" onClick={() => navigate('/learning-certification')}>LEARNING / CERTIFICATION</div>
          <div className="nav-item active">PROFILING</div>
          <div className="nav-item">
            SOURCING
            <div className="nav-subitem">TALENT/EQUIPMENT</div>
          </div>
          <div className="nav-item" onClick={() => navigate('/pitch-upload')}>PITCHING</div>
          <div className="nav-item">
            VALIDATE
            <div className="nav-subitem">PROOF OF CONCEPTS</div>
          </div>
          <div className="nav-item" onClick={() => navigate('/funding-projects')}>FUNDING ZONE</div>
        </nav>

        {/* Main Content Area */}
        <main className="main-content">
          <div className="mandatory-note">
            <span className="required-asterisk">*</span> Mandatory fields
          </div>
          <form className="profile-form" onSubmit={handleSubmit}>
            <div className="form-row">
              {/* Left Column */}
              <div className="form-column">
                <label>
                  <span className="label-with-asterisk">
                    NAME<span className="required-asterisk">*</span>
                  </span>
                  <input
                    type="text"
                    name="name"
                    value={profile.name}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label>
                  <span className="label-with-asterisk">
                    EMAIL<span className="required-asterisk">*</span>
                  </span>
                  <input
                    type="email"
                    name="email"
                    value={profile.email}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label>
                  <span className="label-with-asterisk">
                    PHONE #<span className="required-asterisk">*</span>
                  </span>
                  <input
                    type="tel"
                    name="phone"
                    value={profile.phone}
                    onChange={handleInputChange}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    required
                  />
                </label>

                <label>
                  <span className="label-with-asterisk">
                    EDUCATION<span className="required-asterisk">*</span>
                  </span>
                  <input
                    type="text"
                    name="education"
                    value={profile.education}
                    onChange={handleInputChange}
                    required
                  />
                </label>

                <label>
                  SKILL
                  <input
                    type="text"
                    name="skill"
                    value={profile.skill}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  LINKEDIN
                  <input
                    type="text"
                    name="linkedInUrl"
                    value={profile.linkedInUrl}
                    onChange={handleInputChange}
                    placeholder="Paste LinkedIn URL or type NA"
                  />
                </label>

                <label>
                  INSTAGRAM
                  <input
                    type="text"
                    name="instagramUrl"
                    value={profile.instagramUrl}
                    onChange={handleInputChange}
                    placeholder="Paste Instagram URL or type NA"
                  />
                </label>

                <label>
                  TWITTER / X
                  <input
                    type="text"
                    name="twitterUrl"
                    value={profile.twitterUrl}
                    onChange={handleInputChange}
                    placeholder="Paste Twitter/X URL or type NA"
                  />
                </label>
              </div>

              {/* Right Column */}
              <div className="form-column">
                <label>
                  OCCUPATION / ROLE
                  <input
                    type="text"
                    name="occupation"
                    value={profile.occupation}
                    onChange={handleInputChange}
                    placeholder="e.g. Student / Working Professional / Your Job Designation"
                  />
                </label>
                
                <label>
                  COLLEGE NAME / COMPANY NAME
                  <input
                    type="text"
                    name="company"
                    value={profile.company}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  CITY/LOCATION
                  <input
                    type="text"
                    name="cityLocation"
                    value={profile.cityLocation}
                    onChange={handleInputChange}
                  />
                </label>

                <label>
                  YOUTUBE
                  <input
                    type="text"
                    name="youtubeUrl"
                    value={profile.youtubeUrl}
                    onChange={handleInputChange}
                    placeholder="Paste YouTube URL or type NA"
                  />
                </label>

                <label>
                  PORTFOLIO / WEBSITE
                  <input
                    type="text"
                    name="portfolioUrl"
                    value={profile.portfolioUrl}
                    onChange={handleInputChange}
                    placeholder="Paste portfolio/website URL or type NA"
                  />
                </label>

                <label>
                  TELL US A LITTLE ABOUT YOURSELF !
                  <textarea
                    name="careerAspirations"
                    value={profile.careerAspirations}
                    onChange={handleInputChange}
                    placeholder="Your background, interests, or what you are currently exploring."
                    rows={4}
                  />
                </label>
              </div>
            </div>

            <div className="profile-extra-section">
              <div className="question-block">
                <h3 className="question-title">
                  <span className="label-with-asterisk">
                    Which area are you most interested in exploring?<span className="required-asterisk">*</span>
                  </span>
                </h3>
                <div className="question-subtitle">
                  You can select more than one option.
                </div>
                <div className="options-grid">
                  {[
                    'Cinema / Filmmaking',
                    'Content Creation – Long Form (YouTube, Podcasts, etc.)',
                    'Content Creation – Short Form (Reels, Shorts, TikTok style)',
                    'Entrepreneurship',
                    'Media & Creative Industry',
                  ].map(option => (
                    <label key={option} className="option-pill">
                      <input
                        type="checkbox"
                        checked={(profile.explorationAreas || []).includes(option)}
                        onChange={() => toggleExplorationArea(option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-block">
                <h3 className="question-title">
                  <span className="label-with-asterisk">
                    Are you currently creating content or working in media?<span className="required-asterisk">*</span>
                  </span>
                </h3>
                <div className="options-list">
                  {[
                    'Yes, actively creating',
                    'Just getting started',
                    'Planning to start soon',
                    'Just exploring',
                  ].map(option => (
                    <label key={option} className="option-radio">
                      <input
                        type="radio"
                        name="mediaActivity"
                        value={option}
                        checked={profile.mediaActivity === option}
                        onChange={handleInputChange}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="question-block">
                <h3 className="question-title">
                  <span className="label-with-asterisk">
                    Why do you want to join the MEE School Open House?<span className="required-asterisk">*</span>
                  </span>
                </h3>
                <p className="question-subtitle">
                  Tell us what made you curious about MEE School.
                </p>
                <textarea
                  name="openHouseReason"
                  className="open-house-textarea"
                  rows={3}
                  value={profile.openHouseReason || ''}
                  onChange={handleInputChange}
                  placeholder="Your answer"
                />
              </div>

              <div className="question-block">
                <h3 className="question-title">
                  <span className="label-with-asterisk">
                    How did you hear about this Open House?<span className="required-asterisk">*</span>
                  </span>
                </h3>
                <div className="options-list">
                  {['LinkedIn', 'Instagram', 'WhatsApp / Community', 'Friends / Referral', 'Other'].map(option => (
                    <label key={option} className="option-radio">
                      <input
                        type="radio"
                        name="heardAbout"
                        value={option}
                        checked={profile.heardAbout === option}
                        onChange={handleInputChange}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                </div>
                {profile.heardAbout === 'Other' && (
                  <input
                    type="text"
                    name="heardAboutOther"
                    className="other-input"
                    value={profile.heardAboutOther}
                    onChange={handleInputChange}
                    placeholder="Please specify"
                  />
                )}
              </div>

              <div className="question-block full-width-block">
                <h3 className="question-title">
                  Follow <span style={{ fontWeight: '700' }}>MEE School</span> on Socials
                </h3>
                <p className="question-subtitle">
                  LinkedIn :{' '}
                  <a href="https://www.linkedin.com/company/meeschool/" target="_blank" rel="noopener noreferrer">
                    https://www.linkedin.com/company/meeschool/
                  </a>
                  <br />
                  Instagram :{' '}
                  <a href="https://www.instagram.com/meeschool/" target="_blank" rel="noopener noreferrer">
                    https://www.instagram.com/meeschool/
                  </a>
                </p>
              </div>

              <div className="question-block full-width-block">
                <p className="consent-text">
                  I understand that this registration only expresses my interest, and my participation will be confirmed via
                  email. Only participants who receive a confirmation email will be allowed to attend the event.
                </p>
                <label className="option-radio">
                  <input
                    type="checkbox"
                    name="consentAcknowledged"
                    checked={profile.consentAcknowledged}
                    onChange={handleInputChange}
                  />
                  <span>
                    Yes, I understand and agree.<span className="required-asterisk">*</span>
                  </span>
                </label>
              </div>
            </div>

            <button type="submit" className="submit-btn">SUBMIT</button>
          </form>

          {uploadStatus && <p className="status-message">{uploadStatus}</p>}
        </main>
      </div>

      {/* Footer */}
      <footer className="marketplace-footer">
        <p>© MEESCHOOL, 2026</p>
      </footer>
    </div>
  );
};

export default ProfilePage;

